// hooks/useServiceEnquiry.js
import { useState, useEffect } from 'react';
import { 
  database, 
  ref, 
  set, 
  push, 
  get 
} from '../firebase.config';

const MONTH_NAMES = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec"
];

// Define the service items that can be selected
const SERVICE_ITEMS = [
  "Glovebox Inspection & Preventive Maintenance",
  "Vacuum & Inert Gas System Servicing",
  "Filter & Purification Media Replacement",
  "Seal, Gasket & O-Ring Replacement",
  "Sensor Cleaning & Calibration",
  "Customization & Technical Support",
];

const INTEREST_TYPES = ["Service", "Product", "General"];

export const useServiceEnquiry = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [years, setYears] = useState([]);

  // Get current year and month
  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear().toString();
    const monthIndex = now.getMonth();
    const monthName = MONTH_NAMES[monthIndex];
    return { year, monthName, timestamp: now.toISOString() };
  };

  // Submit a new service enquiry
  const submitServiceEnquiry = async (formData) => {
    setIsLoading(true);
    
    const { year, monthName, timestamp } = getCurrentDate();
    
    const enquiryData = {
      ...formData,
      timestamp,
      year,
      month: monthName,
      monthIndex: MONTH_NAMES.indexOf(monthName),
      read: false,
      status: 'new',
      userAgent: navigator.userAgent,
    };

    try {
      // Add to year/month collection
      const yearMonthRef = ref(database, `serviceEnquiries/${year}/${monthName}`);
      const newEnquiryRef = push(yearMonthRef);
      await set(newEnquiryRef, enquiryData);

      console.log('Service enquiry submitted successfully');
      return { success: true, id: newEnquiryRef.key };
    } catch (error) {
      console.error('Error submitting service enquiry:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Get all enquiries for a specific year and month
  const getEnquiriesByMonth = async (year, monthName) => {
    try {
      const monthRef = ref(database, `serviceEnquiries/${year}/${monthName}`);
      const snapshot = await get(monthRef);
      
      if (snapshot.exists()) {
        const enquiries = [];
        snapshot.forEach((childSnapshot) => {
          enquiries.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        // Sort by timestamp (newest first)
        enquiries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        return enquiries;
      }
      return [];
    } catch (error) {
      console.error('Error fetching service enquiries by month:', error);
      return [];
    }
  };

  // Get all enquiries for a specific year
  const getEnquiriesByYear = async (year) => {
    try {
      const yearRef = ref(database, `serviceEnquiries/${year}`);
      const snapshot = await get(yearRef);
      
      if (snapshot.exists()) {
        const yearData = snapshot.val();
        const allEnquiries = [];
        
        Object.keys(yearData).forEach(month => {
          const monthData = yearData[month];
          Object.keys(monthData).forEach(enquiryId => {
            allEnquiries.push({
              id: enquiryId,
              ...monthData[enquiryId]
            });
          });
        });
        
        // Sort by timestamp (newest first)
        allEnquiries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        return allEnquiries;
      }
      return [];
    } catch (error) {
      console.error('Error fetching service enquiries by year:', error);
      return [];
    }
  };

  // Get ALL enquiries from all years
  const getAllEnquiries = async (filters = {}) => {
    try {
      const allRef = ref(database, 'serviceEnquiries');
      const snapshot = await get(allRef);
      
      if (snapshot.exists()) {
        const allYears = snapshot.val();
        const allEnquiries = [];
        
        // Loop through all years
        Object.keys(allYears).forEach(year => {
          const yearData = allYears[year];
          
          // Loop through all months in the year
          Object.keys(yearData).forEach(month => {
            const monthData = yearData[month];
            
            // Loop through all enquiries in the month
            Object.keys(monthData).forEach(enquiryId => {
              const enquiry = monthData[enquiryId];
              
              // Apply filters
              let shouldInclude = true;
              
              if (filters.year && year !== filters.year) shouldInclude = false;
              if (filters.month && month !== filters.month) shouldInclude = false;
              if (filters.interestItem && enquiry.interestItem !== filters.interestItem) shouldInclude = false;
              if (filters.interestType && enquiry.interestType !== filters.interestType) shouldInclude = false;
              if (filters.status && enquiry.status !== filters.status) shouldInclude = false;
              if (filters.read !== undefined && enquiry.read !== filters.read) shouldInclude = false;
              
              if (shouldInclude) {
                allEnquiries.push({
                  id: enquiryId,
                  ...enquiry
                });
              }
            });
          });
        });
        
        // Sort by timestamp (newest first)
        allEnquiries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        return allEnquiries;
      }
      return [];
    } catch (error) {
      console.error('Error fetching all service enquiries:', error);
      return [];
    }
  };

  // Get total count of ALL enquiries
  const getTotalEnquiryCount = async () => {
    try {
      const snapshot = await get(ref(database, 'serviceEnquiries'));
      if (!snapshot.exists()) return 0;
      
      let total = 0;
      const allYears = snapshot.val();
      
      // Count all enquiries across all years and months
      Object.keys(allYears).forEach(year => {
        const yearData = allYears[year];
        Object.keys(yearData).forEach(month => {
          const monthData = yearData[month];
          total += Object.keys(monthData).length;
        });
      });
      
      return total;
    } catch (error) {
      console.error('Error getting total service enquiry count:', error);
      return 0;
    }
  };

  // Get count by month for a specific year (for charts)
  const getMonthlyCountsForYear = async (year) => {
    try {
      const monthlyCounts = new Array(12).fill(0);
      const yearRef = ref(database, `serviceEnquiries/${year}`);
      const snapshot = await get(yearRef);
      
      if (snapshot.exists()) {
        const yearData = snapshot.val();
        
        MONTH_NAMES.forEach((monthName, index) => {
          if (yearData[monthName]) {
            monthlyCounts[index] = Object.keys(yearData[monthName]).length;
          }
        });
      }
      
      return monthlyCounts;
    } catch (error) {
      console.error('Error getting monthly counts for service enquiries:', error);
      return new Array(12).fill(0);
    }
  };

  // Get count by service item for statistics
  const getServiceItemStats = async (year = null) => {
    try {
      const allEnquiries = year 
        ? await getEnquiriesByYear(year)
        : await getAllEnquiries();
      
      const stats = {};
      SERVICE_ITEMS.forEach(item => {
        stats[item] = 0;
      });
      
      allEnquiries.forEach(enquiry => {
        if (enquiry.interestItem && stats[enquiry.interestItem] !== undefined) {
          stats[enquiry.interestItem]++;
        }
      });
      
      return stats;
    } catch (error) {
      console.error('Error getting service item stats:', error);
      return {};
    }
  };

  // Extract all available years
  const extractYears = async () => {
    try {
      const snapshot = await get(ref(database, 'serviceEnquiries'));
      const yearList = snapshot.exists() 
        ? Object.keys(snapshot.val()).sort((a, b) => b - a)
        : [];
      setYears(yearList);
      return yearList;
    } catch (error) {
      console.error('Error extracting years for service enquiries:', error);
      setYears([]);
      return [];
    }
  };

  // Update enquiry status (mark as read, contacted, etc.)
  const updateEnquiryStatus = async (enquiryId, year, month, updates) => {
    try {
      if (!year || !month) {
        // If year/month not provided, find the enquiry first
        const allEnquiries = await getAllEnquiries();
        const enquiry = allEnquiries.find(e => e.id === enquiryId);
        
        if (!enquiry) {
          return { success: false, error: 'Enquiry not found' };
        }
        
        year = enquiry.year;
        month = enquiry.month;
      }
      
      // Get current enquiry data
      const enquiryRef = ref(database, `serviceEnquiries/${year}/${month}/${enquiryId}`);
      const snapshot = await get(enquiryRef);
      
      if (snapshot.exists()) {
        const currentData = snapshot.val();
        const updatedData = {
          ...currentData,
          ...updates
        };
        
        await set(enquiryRef, updatedData);
        return { success: true };
      }
      
      return { success: false, error: 'Enquiry not found' };
    } catch (error) {
      console.error('Error updating service enquiry status:', error);
      return { success: false, error: error.message };
    }
  };

  // Initialize - fetch years and total count
  useEffect(() => {
    const initialize = async () => {
      await extractYears();
      const total = await getTotalEnquiryCount();
      setTotalCount(total);
    };
    
    initialize();
  }, []);

  return {
    // State
    isLoading,
    totalCount,
    years,
    
    // Constants
    MONTH_NAMES,
    SERVICE_ITEMS,
    INTEREST_TYPES,
    
    // Submit
    submitServiceEnquiry,
    
    // Read operations
    getEnquiriesByMonth,
    getEnquiriesByYear,
    getAllEnquiries,
    getMonthlyCountsForYear,
    getServiceItemStats,
    
    // Statistics
    getTotalEnquiryCount,
    
    // Management
    updateEnquiryStatus,
    extractYears,
  };
};